/**
 * Durable sync queue backed by SQLite (structured ops) + OPFS (media blobs).
 *
 * This module is the *only* thing replaying mutations, and now the only thing
 * claiming to.
 *
 * The sentence that used to be here said a Workbox BackgroundSync queue in
 * `apps/mobile-pwa/vite.config.ts` handled automatic replay at the
 * service-worker layer. It did not: that route took Workbox's default method,
 * so it matched GET, and `kapture-mutation-queue` never held a mutation in its
 * life. The route has since been deleted rather than repaired — see the note
 * in that file for why `method: 'POST'` would have been worse than the bug.
 *
 * So this is the first and last line of defence, which is why the retry
 * schedule, the attempt ceiling and the per-attempt deadline all live here
 * rather than being someone else's problem. React Native has no service worker
 * at all, so a design leaning on Workbox could never have covered more than
 * one of the two clients anyway.
 *
 * What this owns: per-task status, the progress the UI reads, and the
 * reconciliation loop that drains once the radio is back.
 */
import type { SyncOp, SyncStatus, SyncTask } from '@kapture/types';
import { ApiError, NetworkError } from '@kapture/api-client';
import { all, run } from '../db/sqlite';
import { platform } from '../platform';
import { sweepUploadedMedia } from './mediaRetention';
import { toWire } from './wire';

export interface EnqueueInput {
  op: SyncOp;
  entityId: string;
  payload: Record<string, unknown>;
  blobPath?: string | null;
  /** For media ops, the mime the recorder produced. See `SyncTask.mime`. */
  mime?: string | null;
  byteSize?: number;
  /** Server version this op was built against; sent as `If-Match`. */
  baseVersion?: number | null;
  /**
   * Id of a task that must succeed first. The caller states the edge, because
   * the caller is the only layer that knows the causal order.
   */
  dependsOn?: string | null;
}

interface TaskRow {
  id: string;
  op: SyncOp;
  entity_id: string;
  seq: number;
  payload_json: string;
  blob_path: string | null;
  mime: string | null;
  depends_on: string | null;
  byte_size: number;
  status: SyncStatus;
  attempts: number;
  last_error: string | null;
  base_version: string | null;
  created_at: string;
  updated_at: string;
}

function rowToTask(r: TaskRow): SyncTask {
  return {
    id: r.id,
    op: r.op,
    entityId: r.entity_id,
    seq: r.seq,
    payload: JSON.parse(r.payload_json),
    blobPath: r.blob_path,
    mime: r.mime,
    dependsOn: r.depends_on,
    byteSize: r.byte_size,
    status: r.status,
    attempts: r.attempts,
    lastError: r.last_error,
    // Stored as TEXT in SQLite; the wire contract is an integer.
    baseVersion: r.base_version == null ? null : Number(r.base_version),
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export async function enqueue(input: EnqueueInput): Promise<SyncTask> {
  const now = new Date().toISOString();
  const id = platform().ids.taskId();

  // seq is allocated *inside* the INSERT. Reading `MAX(seq) + 1` first and
  // inserting after is two round-trips to the worker, and another caller's
  // INSERT can land between them — two tasks then share a seq and the queue's
  // ordering guarantee quietly stops holding. One statement cannot interleave
  // with itself.
  await run(
    `INSERT INTO sync_tasks
       (id, op, entity_id, seq, payload_json, blob_path, mime, depends_on,
        byte_size, status, attempts, last_error, base_version,
        created_at, updated_at)
     VALUES (?, ?, ?,
             (SELECT COALESCE(MAX(seq), -1) + 1 FROM sync_tasks),
             ?, ?, ?, ?, ?, 'queued', 0, NULL, ?, ?, ?)`,
    [
      id,
      input.op,
      input.entityId,
      JSON.stringify(input.payload),
      input.blobPath ?? null,
      input.mime ?? null,
      input.dependsOn ?? null,
      input.byteSize ?? 0,
      input.baseVersion ?? null,
      now,
      now,
    ],
  );

  // Read back what the INSERT actually allocated. Safe as a second call: the
  // row already exists with its final seq, so nothing can shift it.
  const seqRows = await all<{ seq: number }>(
    `SELECT seq FROM sync_tasks WHERE id = ?`,
    [id],
  );
  const seq = seqRows[0]?.seq ?? 0;

  // New work is a reason to push. Before this, nothing was.
  scheduleDrain();

  return {
    id,
    op: input.op,
    entityId: input.entityId,
    seq,
    payload: input.payload,
    blobPath: input.blobPath ?? null,
    mime: input.mime ?? null,
    dependsOn: input.dependsOn ?? null,
    byteSize: input.byteSize ?? 0,
    status: 'queued',
    attempts: 0,
    lastError: null,
    baseVersion: input.baseVersion ?? null,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * What the /sync screen calls "pending": everything not yet settled, including
 * the rows that need a human. This is a *display* query — see `listDrainable`
 * for what the drain loop is allowed to send.
 */
export async function listPending(limit = 50): Promise<SyncTask[]> {
  const rows = await all<TaskRow>(
    `SELECT * FROM sync_tasks
     WHERE status IN ('queued', 'retrying', 'uploading', 'conflict', 'failed')
     ORDER BY seq ASC
     LIMIT ?`,
    [limit],
  );
  return rows.map(rowToTask);
}

/**
 * Tasks the drain may actually dispatch right now.
 *
 * Deliberately not `listPending`. That query is what the UI shows, and it
 * includes `conflict` and `failed` because a person needs to see them — but
 * `_drain()` was iterating the same list and only skipping `conflict`, so
 * every permanently-failed task (a 403, a 422) was re-sent on every single
 * drain, forever. `countPending()` excludes `failed`, so the badge never
 * showed it either: an invisible retry loop against the API.
 *
 * `failed` is now terminal. It leaves that state only when a person retries it
 * (`retryTask`) or discards it.
 *
 * `uploading` stays eligible: a task orphaned mid-flight by a tab close must be
 * picked up again, and the server dedupes it on `Idempotency-Key`.
 */
async function listDrainable(limit = 50): Promise<SyncTask[]> {
  const rows = await all<TaskRow>(
    `SELECT * FROM sync_tasks
     WHERE status IN ('queued', 'retrying', 'uploading')
       AND (next_attempt_at IS NULL OR next_attempt_at <= ?)
       -- A task waits for the one it depends on. Phrased as "no unsettled
       -- dependency" rather than "a succeeded dependency exists" so that a
       -- dependency already pruned by pruneSucceeded() releases its
       -- dependant instead of stranding it for ever.
       AND (depends_on IS NULL OR NOT EXISTS (
             SELECT 1 FROM sync_tasks d
              WHERE d.id = sync_tasks.depends_on AND d.status <> 'succeeded'))
     ORDER BY seq ASC
     LIMIT ?`,
    [new Date().toISOString(), limit],
  );
  return rows.map(rowToTask);
}

export async function countPending(): Promise<number> {
  const rows = await all<{ n: number }>(
    `SELECT COUNT(*) AS n FROM sync_tasks WHERE status IN ('queued','retrying','uploading','conflict')`,
  );
  return rows[0]?.n ?? 0;
}

/** All tasks regardless of status (bounded recent window). */
export async function listAllTasks(limit = 200): Promise<SyncTask[]> {
  const rows = await all<TaskRow>(
    `SELECT * FROM sync_tasks ORDER BY seq DESC LIMIT ?`,
    [limit],
  );
  return rows.map(rowToTask);
}

export async function getTask(id: string): Promise<SyncTask | null> {
  const rows = await all<TaskRow>(`SELECT * FROM sync_tasks WHERE id = ?`, [id]);
  return rows[0] ? rowToTask(rows[0]) : null;
}

export interface RollupCounts {
  queued: number;
  retrying: number;
  uploading: number;
  succeeded: number;
  conflict: number;
  failed: number;
}

/** Queue counts bucketed by status — drives the /sync rollup cards. */
export async function rollupCounts(): Promise<RollupCounts> {
  const rows = await all<{ status: SyncStatus; n: number }>(
    `SELECT status, COUNT(*) AS n FROM sync_tasks GROUP BY status`,
  );
  const base: RollupCounts = {
    queued: 0,
    retrying: 0,
    uploading: 0,
    succeeded: 0,
    conflict: 0,
    failed: 0,
  };
  for (const r of rows) base[r.status] = r.n;
  return base;
}

/** Flip a retrying/failed task back to queued so the next drain picks it up. */
export async function retryTask(id: string): Promise<void> {
  await markStatus(id, 'queued', null);
}

/** Abandon a failed task — unwanted without server reconciliation. */
export async function discardTask(id: string): Promise<void> {
  await run(`DELETE FROM sync_tasks WHERE id = ?`, [id]);
}

/**
 * Resolve a conflict task against real server state.
 *
 * `keep-local` re-bases onto the version the server reported in the 409 and
 * re-queues, so the next drain overwrites the server row deliberately.
 * Re-sending the original base version would just conflict again, forever.
 *
 * `keep-remote` fetches the server's row, applies it locally, and drops the
 * task — the local edit is abandoned, which is what the user asked for.
 */
export async function resolveConflict(
  id: string,
  resolution: 'keep-local' | 'keep-remote',
): Promise<void> {
  const task = await getTask(id);
  if (!task) return;

  if (resolution === 'keep-local') {
    // `__conflictResolution` was a placeholder from before there was a server;
    // nothing consumes it, so strip it rather than keep shipping it.
    const { __conflictResolution: _discarded, ...payload } = task.payload;
    await run(
      `UPDATE sync_tasks
       SET status = 'queued', last_error = NULL, payload_json = ?,
           base_version = COALESCE(server_version, base_version),
           next_attempt_at = NULL, updated_at = ?
       WHERE id = ?`,
      [JSON.stringify(payload), new Date().toISOString(), id],
    );
    return;
  }

  const fetched = await fetchServerRow(task);

  // Order matters. Fetch first, because a failure there must leave the
  // conflict intact rather than discarding the local edit for nothing. Then
  // drop the task, because the pull path's queue guard skips rows with
  // pending work — applying while this task still existed would be a no-op.
  await discardTask(id);

  if (fetched) {
    const { applyServerRow } = await import('./pull');
    await applyServerRow(fetched.kind, fetched.row);
  }
}

/** The server's copy of whatever this task was writing, if it's readable. */
async function fetchServerRow(
  task: SyncTask,
): Promise<{ kind: 'session' | 'capture'; row: unknown } | null> {
  switch (task.op) {
    case 'session.upsert':
    case 'session.submit': {
      const res = await platform().api.get<{ row: unknown }>(`/sessions/${task.entityId}`);
      return { kind: 'session', row: res.row };
    }
    case 'capture.upsert':
    case 'capture.delete':
    case 'media.upload': {
      const res = await platform().api.get<{ row: unknown }>(`/captures/${task.entityId}`);
      return { kind: 'capture', row: res.row };
    }
    default:
      // fieldValue.upsert and signature.upload have no read endpoint — spec §4
      // lists no single-row pull for either. "Keep remote" can only mean
      // "stop pushing mine"; the local row stays stale until the API grows a
      // field-value read. Flagged for Stage 7.
      return null;
  }
}

async function markStatus(
  id: string,
  status: SyncStatus,
  lastError?: string | null,
  incrementAttempts = false,
  nextAttemptAt: string | null = null,
): Promise<void> {
  const now = new Date().toISOString();
  await run(
    `UPDATE sync_tasks
     SET status = ?, last_error = ?, updated_at = ?, next_attempt_at = ?,
         attempts = attempts + CASE WHEN ? THEN 1 ELSE 0 END
     WHERE id = ?`,
    [status, lastError ?? null, now, nextAttemptAt, incrementAttempts ? 1 : 0, id],
  );
}

/**
 * How long to wait before attempt number `attempts`.
 *
 * Exponential from 2s, doubling, capped at 5 minutes. Pure and exported so the
 * schedule can be asserted directly instead of inferred from timing.
 */
export function backoffDelayMs(attempts: number): number {
  const base = 2_000 * 2 ** Math.max(0, attempts - 1);
  return Math.min(base, 5 * 60_000);
}

/**
 * Attempts allowed before a retryable failure is treated as permanent.
 *
 * At the schedule above, eight attempts spans roughly twenty minutes of real
 * retrying — long enough to ride out a tunnel or a captive portal, short
 * enough that a genuinely broken task stops burning radio.
 */
export const MAX_ATTEMPTS = 8;

/**
 * When a retrying task becomes eligible again.
 *
 * Jittered by up to 20% so a queue that all failed together — the usual case,
 * because they failed for the same reason — does not march back to the server
 * in lockstep.
 */
function nextAttemptAt(attempts: number, now = Date.now()): string {
  const delay = backoffDelayMs(attempts);
  return new Date(now + delay + Math.random() * delay * 0.2).toISOString();
}

/**
 * Delete settled tasks past their retention window.
 *
 * `succeeded` rows were never removed, so `sync_tasks` grew without bound and
 * every `enqueue` scanned an ever-larger `MAX(seq)`. On a phone that runs for
 * months this is the difference between a queue and a log. Returns the number
 * of rows removed.
 */
export async function pruneSucceeded(retentionDays = 7): Promise<number> {
  const cutoff = new Date(Date.now() - retentionDays * 86_400_000).toISOString();
  const before = await all<{ n: number }>(
    `SELECT COUNT(*) AS n FROM sync_tasks WHERE status = 'succeeded' AND updated_at < ?`,
    [cutoff],
  );
  const n = before[0]?.n ?? 0;
  if (n > 0) {
    await run(`DELETE FROM sync_tasks WHERE status = 'succeeded' AND updated_at < ?`, [
      cutoff,
    ]);
  }
  return n;
}

/**
 * Drain the queue once. Intended to be called when we detect `online`,
 * or on a timer from the sync store. Returns the number of tasks that
 * finished successfully.
 */
/**
 * Whether enqueuing work should schedule a push.
 *
 * Off by default and switched on by `startSyncTriggers()`, so unit tests that
 * exercise `enqueue`/`drain` directly keep their existing deterministic
 * behaviour instead of racing a background drain.
 */
let autoDrain = false;
let drainTimer: ReturnType<typeof setTimeout> | null = null;

export function setAutoDrain(on: boolean): void {
  autoDrain = on;
  if (!on && drainTimer) {
    clearTimeout(drainTimer);
    drainTimer = null;
  }
}

/**
 * Ask for a push soon.
 *
 * The queue had four triggers and none of them was "there is work to send":
 * the only automatic one was the offline→online transition, so an inspector
 * who never lost signal never synced, and their work sat on one device while
 * the header read "Online".
 *
 * Debounced because one user action enqueues several tasks — creating a
 * session writes the session, then its captures, then their media — and each
 * should not start its own drain. Deliberately calls `drain()` rather than
 * `syncNow()`: new local work is a reason to push, not a reason to pull, and
 * pulling on every capture is exactly the battery cost the no-timer design
 * was avoiding.
 */
export function scheduleDrain(delayMs = 400): void {
  if (!autoDrain) return;
  if (!platform().net.isOnline()) return;
  if (drainTimer) clearTimeout(drainTimer);
  drainTimer = setTimeout(() => {
    drainTimer = null;
    void drain().catch(() => {
      // Per-task outcomes are already recorded on the rows; a failed drain
      // here must not become an unhandled rejection.
    });
  }, delayMs);
}

/**
 * The drain currently in flight, if any.
 *
 * Without this, two triggers firing together — reconnecting while a capture
 * is being saved, say — each ran the loop over the same rows and pushed every
 * task twice. The server deduped on `Idempotency-Key`, so nothing was written
 * twice, but the attempt counters and the bandwidth were both real.
 */
let inFlight: Promise<number> | null = null;

/**
 * Media ops. Named once because three separate rules key off "is this bytes
 * or is this a row" — the deadline, the media budget, and the write-back.
 */
const MEDIA_OPS: ReadonlySet<SyncOp> = new Set<SyncOp>(['media.upload', 'signature.upload']);

export interface DrainOptions {
  /**
   * Stop after attempting this many tasks. For a caller with a budget rather
   * than a goal — a background task that must return before the OS kills it.
   */
  maxTasks?: number;
  /**
   * Stop *starting* new tasks once this much wall clock has passed. Work
   * already in flight finishes; cutting a request off mid-upload would waste
   * the bytes already sent, and its own `timeoutMs` bounds it anyway.
   */
  deadlineMs?: number;
  /**
   * How many media uploads this drain may send. Only `0` and "not 0" differ
   * today, because the drain is sequential — see `_drain`.
   *
   * `0` is the one that matters, and it exists for
   * `apps/mobile/src/background/syncTask.ts`. `apps/mobile/README.md` says
   * outright that large media uploads are foreground-only because a
   * background task cannot promise to survive one; until now nothing enforced
   * that, so a background drain picked up a 1.25 MB clip like any other row
   * and was killed halfway through it. Passing `0` makes the README's claim
   * true: the background pass syncs rows, and the bytes wait for a foreground
   * the user is actually looking at.
   */
  maxMediaInflight?: number;
}

export async function drain(options: DrainOptions = {}): Promise<number> {
  // Note the shared promise ignores `options`: a second caller with different
  // options joins the drain already running rather than starting a competing
  // one. That is the right trade — two concurrent drains over the same rows
  // is the bug `inFlight` exists to prevent — but it does mean a background
  // caller asking for `maxMediaInflight: 0` can join a foreground drain that
  // is already uploading. Harmless, because the foreground drain is the one
  // allowed to do it.
  if (inFlight) return inFlight;
  inFlight = _drain(options).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * How long one push may take before we cut it off.
 *
 * Derived from the payload rather than fixed, because a fixed deadline is
 * wrong at both ends: 30 seconds strands a 10 MB clip on a rural connection
 * that would have finished, and five minutes lets a stalled row hold the
 * queue for five minutes. The slope assumes ~50 kB/s — a bad 3G, not a good
 * one, because the whole point is to still succeed where the connection is
 * poor and only give up where it is broken — plus a flat 30s of setup slack
 * for DNS, TLS and the server's own work.
 *
 * A photograph gets ~33s, a 10-second clip ~55s, and the 8 MB ceiling ~3
 * minutes. Capped at 10 minutes so no single task can hold the queue longer
 * than that whatever its size.
 */
export function pushTimeoutMs(byteSize: number): number {
  const BYTES_PER_SECOND = 50_000;
  const SETUP_SLACK_MS = 30_000;
  const CEILING_MS = 10 * 60_000;
  const transfer = Math.ceil(Math.max(0, byteSize) / BYTES_PER_SECOND) * 1_000;
  return Math.min(CEILING_MS, SETUP_SLACK_MS + transfer);
}

/**
 * How many times one drain will re-query for work it has just unblocked.
 *
 * A photo enqueues `capture.upsert` and a `media.upload` that depends on it.
 * On the first pass the upload is not drainable — its dependency has not
 * succeeded yet — so a single-snapshot drain finished having uploaded the row
 * and nothing else, and the bytes then waited for some unrelated trigger
 * (a reconnect, a foreground, the next capture) that in a quiet app never
 * came. Passing again after settling work is what turns a dependency edge
 * into something that actually flows.
 *
 * Bounded, and each pass must make progress, so a task that keeps re-queueing
 * itself cannot spin here.
 */
const MAX_DRAIN_PASSES = 5;

async function _drain(options: DrainOptions = {}): Promise<number> {
  const { maxTasks = Infinity, deadlineMs, maxMediaInflight } = options;
  const stopAt = deadlineMs == null ? Infinity : Date.now() + deadlineMs;
  const sendMedia = maxMediaInflight !== 0;
  let succeeded = 0;
  let attempted = 0;

  for (let pass = 0; pass < MAX_DRAIN_PASSES; pass++) {
    if (attempted >= maxTasks || Date.now() >= stopAt) break;

    const tasks = await listDrainable();
    if (tasks.length === 0) break;

    let settledThisPass = 0;
    for (const task of tasks) {
      if (attempted >= maxTasks || Date.now() >= stopAt) break;
      // Skipped, not failed: the row stays exactly as it was and the next
      // foreground drain picks it up. Marking it would be a lie — nothing
      // was attempted.
      if (!sendMedia && MEDIA_OPS.has(task.op)) continue;

      attempted++;
      await markStatus(task.id, 'uploading', null, true);
      try {
        const result = await dispatch(task);
        await recordServerVersion(task, result?.version);
        await recordUpload(task, result);
        await markStatus(task.id, 'succeeded');
        succeeded++;
        settledThisPass++;
      } catch (err) {
        await handleFailure(task, err);
        settledThisPass++;
      }
    }

    // No progress means another pass would see the same rows. The only rows
    // left are ones waiting on a clock (`next_attempt_at`), on a dependency
    // that did not succeed, or on a foreground drain to carry their bytes —
    // and none of those is helped by asking again now.
    if (settledThisPass === 0) break;
  }

  // Cheap, and the only place with a natural reason to run: we just settled
  // rows, so some of them may now be old enough to drop.
  await pruneSucceeded().catch(() => {
    // Retention is housekeeping. Failing it must not fail the drain.
  });

  // Same reasoning, one layer down: a drain is the only moment new bytes
  // become reclaimable, because it is the only thing that writes
  // `captures.uploaded_at`. Costs one indexed SUM when the device is inside
  // its budget, which is nearly always.
  await sweepUploadedMedia().catch(() => {
    // Never at the expense of the sync it just did.
  });
  return succeeded;
}

/**
 * Decide what a failed push means for the task (spec §8).
 *
 * The distinction that matters most is 401 vs 403: an expired token is the
 * normal outcome of a week offline and the work is still good, while "not
 * writer of record" means the row moved on without us and no amount of
 * retrying will help.
 */
async function handleFailure(task: SyncTask, err: unknown): Promise<void> {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      await markConflict(task.id, err);
      return;
    }
    // 401 after the client already tried a refresh: still transient. Failing
    // here would discard work that only needed a fresh token.
    const retryable = err.status === 401 || err.isRetryable;
    if (retryable) {
      await scheduleRetry(task, err.message);
      return;
    }
    await markStatus(task.id, 'failed', err.message);
    return;
  }

  if (err instanceof NetworkError) {
    await scheduleRetry(task, err.message);
    return;
  }

  // Anything else is a bug on our side — a malformed task, a missing blob.
  // Retrying it forever would just hide it.
  await markStatus(task.id, 'failed', err instanceof Error ? err.message : String(err));
}

/**
 * Put a transiently-failed task back with a delay, or give up on it.
 *
 * `attempts` was already incremented when the drain marked the task
 * `uploading`, so it counts this attempt. Past the ceiling the task becomes
 * `failed` — terminal, visible on /sync, and retryable by a person — rather
 * than retrying at the same cadence indefinitely.
 */
async function scheduleRetry(task: SyncTask, message: string): Promise<void> {
  const attempts = task.attempts + 1;
  if (attempts >= MAX_ATTEMPTS) {
    await markStatus(
      task.id,
      'failed',
      `${message} (gave up after ${attempts} attempts)`,
    );
    return;
  }
  await markStatus(task.id, 'retrying', message, false, nextAttemptAt(attempts));
}

async function markConflict(id: string, err: ApiError): Promise<void> {
  await run(
    `UPDATE sync_tasks
     SET status = 'conflict', last_error = ?, server_version = ?, updated_at = ?
     WHERE id = ?`,
    [err.message, err.serverVersion, new Date().toISOString(), id],
  );
}

/**
 * Response envelope shared by every mutating endpoint (spec §4), plus the
 * fields a media upload answers with instead of a version.
 *
 * `POST /media/{capture_id}` returns `{url, media_id, byte_size, sha256}` and
 * no `version` at all — the bytes are a new row of their own, not a new
 * revision of the capture. Both shapes share this type because `dispatch`
 * has one return type for seven ops and splitting it would push a union
 * through every branch to describe two optional fields.
 */
interface WithVersion {
  row?: Record<string, unknown>;
  version?: number;
  /** Media ops only: the server's id for the stored bytes. */
  media_id?: string;
  /** Media ops only: where the bytes can be fetched from. */
  url?: string;
}

/**
 * Field-value ops address two ids, so they are queued under the composite
 * `{sessionId}:{fieldId}`. Splitting on the first colon keeps field ids
 * containing colons intact.
 */
function splitFieldEntityId(entityId: string): { sessionId: string; fieldId: string } {
  const separator = entityId.indexOf(':');
  if (separator === -1) {
    throw new Error(
      `fieldValue op has a non-composite entity id: ${entityId} (expected "{sessionId}:{fieldId}")`,
    );
  }
  return {
    sessionId: entityId.slice(0, separator),
    fieldId: entityId.slice(separator + 1),
  };
}

async function uploadMultipart(task: SyncTask, path: string): Promise<WithVersion> {
  if (!task.blobPath) throw new Error(`Media op ${task.op} missing blobPath`);
  const form = new FormData();
  // The media store appends the part, because how a file reaches FormData is
  // exactly what differs between platforms: the browser reads a Blob out of
  // OPFS, while React Native passes `{uri, name, type}` so the native layer
  // streams from disk instead of loading a photo into memory.
  await platform().media.appendToForm(form, 'file', task.blobPath, task.mime ?? undefined);
  form.append('payload', JSON.stringify(task.payload));
  return platform().api.post<WithVersion>(path, form, {
    idempotencyKey: task.id,
    timeoutMs: pushTimeoutMs(task.byteSize),
  });
}

/**
 * Send one task. Returns the server's response so the caller can write the
 * new version back — without that, the next write to the same row re-sends a
 * stale base version and conflicts forever.
 */
async function dispatch(task: SyncTask): Promise<WithVersion> {
  // `Idempotency-Key` is the task id: replaying a task the server already
  // applied returns the original response instead of writing twice.
  // `byteSize` is 0 for every op that is not media, so those all get the flat
  // 30-second floor — which is the right shape: a JSON row that has not been
  // answered in half a minute is not slow, it is stuck.
  const opts = {
    idempotencyKey: task.id,
    ifMatch: task.baseVersion,
    timeoutMs: pushTimeoutMs(task.byteSize),
  };

  switch (task.op) {
    case 'media.upload':
      return uploadMultipart(task, `/media/${task.entityId}`);
    case 'signature.upload':
      return uploadMultipart(task, `/signatures/${task.entityId}`);
    case 'capture.upsert':
      return platform().api.put<WithVersion>(
        `/captures/${task.entityId}`,
        toWire(task.op, task.payload),
        opts,
      );
    case 'capture.delete':
      return platform().api.delete<WithVersion>(`/captures/${task.entityId}`, opts);
    case 'session.upsert':
      return platform().api.put<WithVersion>(
        `/sessions/${task.entityId}`,
        toWire(task.op, task.payload),
        opts,
      );
    case 'session.submit':
      return platform().api.post<WithVersion>(
        `/sessions/${task.entityId}/submit`,
        toWire(task.op, task.payload),
        opts,
      );
    case 'fieldValue.upsert': {
      const { sessionId, fieldId } = splitFieldEntityId(task.entityId);
      return platform().api.put<WithVersion>(
        `/sessions/${sessionId}/fields/${fieldId}`,
        toWire(task.op, task.payload),
        opts,
      );
    }
  }
}

/** Local table a successful push should stamp the new version onto. */
function versionTarget(task: SyncTask): { sql: string; bind: unknown[] } | null {
  switch (task.op) {
    case 'session.upsert':
    case 'session.submit':
      return { sql: `UPDATE sessions SET version = ? WHERE id = ?`, bind: [task.entityId] };
    case 'capture.upsert':
      return { sql: `UPDATE captures SET version = ? WHERE id = ?`, bind: [task.entityId] };
    case 'fieldValue.upsert': {
      const { sessionId, fieldId } = splitFieldEntityId(task.entityId);
      return {
        sql: `UPDATE field_values SET version = ? WHERE session_id = ? AND field_id = ?`,
        bind: [sessionId, fieldId],
      };
    }
    // capture.delete removes the row; media ops version the media, not a
    // local row we track.
    default:
      return null;
  }
}

async function recordServerVersion(task: SyncTask, version: number | undefined): Promise<void> {
  if (version == null) return;
  const target = versionTarget(task);
  if (!target) return;
  await run(target.sql, [version, ...target.bind]);
}

/**
 * Stamp a successful media upload onto the local capture row.
 *
 * `captures.media_id` and `captures.media_url` have existed since sync
 * migration v2 and nothing has ever written to either. `recordServerVersion`
 * asks `versionTarget()` where to put the response, media ops return `null`
 * there — correctly, since they carry no version — and so the whole upload
 * response was discarded. The consequences were not cosmetic:
 *
 *   - the phone had no local record that the bytes were safely on the server,
 *     which is the precondition for ever reclaiming the blob, so OPFS grew
 *     without bound;
 *   - a reviewer opening a capture on another device had a `media_id` from
 *     the pull path, while the device that recorded it did not;
 *   - `uploaded_at` (migration v6) is the only durable evidence of *when*.
 *
 * `signature.upload` is deliberately not handled: a signature is not a
 * capture, and there is no local row with these columns to write to.
 */
async function recordUpload(task: SyncTask, result: WithVersion | undefined): Promise<void> {
  if (task.op !== 'media.upload') return;
  if (!result?.media_id) return;
  await run(
    `UPDATE captures SET media_id = ?, media_url = ?, uploaded_at = ? WHERE id = ?`,
    [result.media_id, result.url ?? null, new Date().toISOString(), task.entityId],
  );
}
